import * as React from 'react';
import useWidget from '@Utils/core/useWidget';
import { WidgetId } from '@Utils/edge/constants';
import copyToClipboard from '@Utils/facade/copyToClipboard';
import useDefineDevice from '@Utils/core/hooks/useDefineDevice';
import { ButtonSizes } from '@Utils/shared/components/Button-v3';
import CopyButton from './CopyButton';
import GenerateButton from './GenerateButton';
import * as Styled from './Nickname.styled';
import useValidateNickname from '../../hooks/useValidateNickname';
import GeneratorContext from '../../GeneratorContext';

const TIMEOUT_VALUE = 10;

const Nickname: React.FC = () => {
  const widgetGeneratorGlobalV3 = useWidget(WidgetId.GeneratorGlobalV3);

  const context = React.useContext(GeneratorContext);
  const { isMobileDevice, isTouchDevice } = useDefineDevice();
  const { showErrorMessage } = React.useContext(GeneratorContext);

  const { validator } = useValidateNickname();

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nickname = e.target.value;
    context.setGeneratedNickname(nickname);
    validator(nickname);
  };

  const timeoutRef = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    if (context.generatedNickname && isMobileDevice) {
      timeoutRef.current = setTimeout(() => {
        copyToClipboard(context.generatedNickname);
      }, TIMEOUT_VALUE);
    }

    return () => {
      timeoutRef && timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [context.generatedNickname, isMobileDevice]);

  /**
   * Handle Play Now button click
   *
   * Opens the correct profile URL with the generated nickname
   */
  function handlePlayNow() {
    try {
      const lang = window.plariumSettings?.languageCode ?? 'en';
      const nickName = context.generatedNickname ?? '';
      const baseUrl = `https://portal.plarium.com/${lang}/profile/`;

      const params = new URLSearchParams(window.plariumNicknameGeneratorParams?.desktop || {
        appid: '27',
        plid: '442422',
      });

      if (nickName) {
        params.append('nickname', encodeURIComponent(nickName));
      }

      const url = `${baseUrl}?${params.toString()}`;
      window.location.href = url;
    } catch (error) {
      console.error('Error opening profile URL:', error);
    }
  }

  if (!context.isGenerated && !context.generatedNickname) {
    return <GenerateButton />;
  }

  return (
    <div>
      <GenerateButton />
      <Styled.WrapperNickname>
        {!isMobileDevice && <Styled.Info html={widgetGeneratorGlobalV3.strings.playGameText} />}
        <Styled.NicknameInfo isTouchDevice={isTouchDevice}>
          <Styled.Nickname isTouchDevice={isTouchDevice}>
            <Styled.Input
              isTouchDevice={isTouchDevice}
              name="nickname"
              value={context.generatedNickname}
              onChange={onChangeInput}
            />
            {!isMobileDevice && <Styled.Avatar src={context.avatarInput} />}
            {!context.showErrorMessage && !context.isCopied && !isMobileDevice ? (
              <CopyButton setIsCopied={context.setIsCopied} />
            ) : null}
            {isMobileDevice && <CopyButton setIsCopied={context.setIsCopied} />}
            {context.isCopied && !context.showErrorMessage && !isMobileDevice && (
              <Styled.ActionText isTouchDevice={isTouchDevice}>
                {widgetGeneratorGlobalV3.strings.successCopied}
              </Styled.ActionText>
            )}
            {context.showErrorMessage && (
              <Styled.ActionText isTouchDevice={isTouchDevice}>
                {widgetGeneratorGlobalV3.strings.validateError}
              </Styled.ActionText>
            )}
          </Styled.Nickname>
          {!context.showErrorMessage && isMobileDevice && (
            <Styled.ActionText isTouchDevice={isTouchDevice}>
              {widgetGeneratorGlobalV3.strings.successCopied}
            </Styled.ActionText>
          )}
          {!isMobileDevice ? (
            <Styled.PlayNowButton
              size={ButtonSizes.large}
              disabled={showErrorMessage}
              onClick={handlePlayNow}
            >
              {widgetGeneratorGlobalV3.strings.playWithButton}
            </Styled.PlayNowButton>
          ) : null}
        </Styled.NicknameInfo>
      </Styled.WrapperNickname>
    </div>
  );
};

export default Nickname;
